/**
 * Sprint 23.8 — Connections sidebar block (block 3).
 *
 * A chip wrap of the workspace's connections — MCP servers and wrapped APIs
 * alike (first 5 + a "+N" overflow), or an empty-state prompt. The whole block
 * links to `/connections`.
 */

import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { SidebarBlock } from "../shell/SidebarBlock";
import { useWorkspaceOverview } from "../../hooks/use-workspace-overview";

const VISIBLE_LIMIT = 5;

export function ConnectionsSummaryBlock() {
  const navigate = useNavigate();
  const { data } = useWorkspaceOverview();
  const connections = data?.connections ?? [];

  const head = connections.slice(0, VISIBLE_LIMIT);
  const overflow = Math.max(0, connections.length - head.length);

  return (
    <SidebarBlock title="Connections">
      {connections.length === 0 ? (
        <button type="button" style={emptyBtn} onClick={() => navigate("/connections")}>
          No connections yet. + Connect a tool
        </button>
      ) : (
        <div style={chips}>
          {head.map((c) => (
            <button
              key={`${c.kind}:${c.id}`}
              type="button"
              style={chip}
              title={c.name}
              onClick={() =>
                navigate(c.kind === "mcp" ? `/connections/m/${c.id}` : `/connections/w/${c.id}`)
              }
            >
              {c.name}
            </button>
          ))}
          {overflow > 0 && (
            <button type="button" style={moreChip} onClick={() => navigate("/connections")}>
              +{overflow}
            </button>
          )}
        </div>
      )}
    </SidebarBlock>
  );
}

const chips: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
};

const chip: CSSProperties = {
  fontSize: 11,
  padding: "3px 8px",
  borderRadius: 999,
  background: "var(--color-background-primary)",
  borderWidth: "0.5px",
  borderStyle: "solid",
  borderColor: "var(--color-border-tertiary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const moreChip: CSSProperties = {
  ...chip,
  color: "var(--color-text-tertiary)",
};

const emptyBtn: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  background: "transparent",
  border: "none",
  padding: 0,
  textAlign: "left",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  lineHeight: 1.5,
};
